import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Supabase's default browser lock can remain orphaned after a tab/PWA is killed.
// In that state auth initialization waits forever and can prevent every later auth
// operation from settling. Venaqqi deliberately serializes its own UI operations,
// so running the critical section directly is safer than leaving the app unusable.
export const noOpLock = async (_name, _acquireTimeout, fn) => fn();

export const withTimeout = (operation, timeoutMs, message) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error(message)), timeoutMs);
  });
  return Promise.race([operation, timeout]).finally(() => clearTimeout(timeoutId));
};

export const clearLocalSupabaseSession = () => {
  if (typeof window === 'undefined') return;
  let projectRef;
  try { projectRef = new URL(supabaseUrl).hostname.split('.')[0]; } catch { return; }
  const prefix = `sb-${projectRef}-auth-token`;
  Object.keys(window.localStorage)
    .filter(key => key === prefix || key.startsWith(`${prefix}.`))
    .forEach(key => window.localStorage.removeItem(key));
};

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    // OAuth PKCE and password recovery both return credentials in the URL.
    // Supabase must consume them before getSession restores the application.
    detectSessionInUrl: true,
    lock: noOpLock,
  },
});

// Supabase warns against awaiting Supabase work from inside onAuthStateChange.
// Existing application listeners may be async, so defer their execution until
// after Supabase's internal auth callback has returned. This keeps the auth
// callback synchronous and prevents subsequent REST calls from deadlocking.
const rawOnAuthStateChange = supabase.auth.onAuthStateChange.bind(supabase.auth);
supabase.auth.onAuthStateChange = (callback) => rawOnAuthStateChange((event, session) => {
  setTimeout(() => {
    Promise.resolve(callback(event, session)).catch(error => {
      // Keep listener failures from becoming unhandled rejections. Individual
      // screens still own their user-facing error handling.
      if (process.env.NODE_ENV !== 'production') console.error('Deferred auth listener error:', error);
    });
  }, 0);
});
